import { Controller } from "@hotwired/stimulus"

// "Share with other nameplates": a searchable list of cars, plus one full
// version/year/region rule-picker per checked car. Checking a car lazy-loads
// its picker (defaulting to all its motorizations) from the server; unchecking
// removes the picker and its hidden inputs, which unshares that car on save.
//
// The heavy lifting inside each picker (resolving versions × years × regions
// into concrete model:region pairs) is done by the nested `feature-form`
// controller — this controller only manages which pickers exist.
export default class extends Controller {
  static targets = ["search", "row", "checkbox", "blocks", "block", "count", "empty"]
  static values = {
    url:       String,          // rule_picker endpoint
    featureId: String,
    loading:   { type: String, default: "…" }
  }

  connect() {
    this.updateCount()
  }

  filter() {
    const q = this.searchTarget.value.trim().toLowerCase()
    let visible = 0
    this.rowTargets.forEach(row => {
      const match = row.dataset.label.toLowerCase().includes(q)
      row.hidden = !match
      if (match) visible++
    })
    if (this.hasEmptyTarget) this.emptyTarget.hidden = visible > 0
  }

  toggleCar(event) {
    const cb = event.currentTarget
    if (cb.checked) {
      this.addBlock(cb.value)
    } else {
      this.removeBlock(cb.value)
    }
    this.updateCount()
  }

  addBlock(carId) {
    if (this.blockFor(carId) || this._loadingEl(carId)) return

    const placeholder = document.createElement("div")
    placeholder.dataset.loadingCar = carId
    placeholder.className = "text-xs text-base-content/40 px-3 py-2"
    placeholder.textContent = this.loadingValue
    this.blocksTarget.appendChild(placeholder)

    const url = `${this.urlValue}?scope=shared&car_id=${encodeURIComponent(carId)}&feature_id=${encodeURIComponent(this.featureIdValue)}`
    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest", "Accept": "text/html" } })
      .then(r => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
      .then(html => { placeholder.outerHTML = html })
      .catch(() => { placeholder.remove() })
  }

  removeBlock(carId) {
    const block = this.blockFor(carId)
    if (block) block.remove()
    const loading = this._loadingEl(carId)
    if (loading) loading.remove()
  }

  blockFor(carId) {
    return this.blockTargets.find(b => b.dataset.carId === carId)
  }

  _loadingEl(carId) {
    return this.blocksTarget.querySelector(`[data-loading-car="${CSS.escape(carId)}"]`)
  }

  updateCount() {
    if (this.hasCountTarget) {
      this.countTarget.textContent = this.checkboxTargets.filter(c => c.checked).length
    }
  }
}
